import React, { useMemo, useState } from "react";
import { Alert, Button, DatePicker, Input, Switch, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { SorterResult } from "antd/es/table/interface";
import { DownloadOutlined, SearchOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { toast } from "react-toastify";
import useLang from "../../../../hooks/useLang";
import {
	ClientWithoutOrders,
	ClientsWithoutOrdersReport,
	SortDirection,
	WithoutOrdersQuery,
	exportClientsWithoutOrders,
	getClientsWithoutOrders,
	readBlobErrorMessage,
	saveWorkbook,
} from "../../../../api/client-reports";
import BulkOpsHeader from "../../components/BulkOpsHeader";
import { PRIMARY_BUTTON_STYLE } from "../../components/styles";
import {
	countryTotalsColumns,
	daysTagColor,
	formatDate,
	formatDateTime,
	formatNumber,
	identityColumns,
	toApiSort,
	toWireDate,
} from "../reportFormat";

const { RangePicker } = DatePicker;

const DEFAULT_PAGE_SIZE = 50;

const WithoutOrders = () => {
	const { strings } = useLang("clientReports");
	const common = strings?.common;
	const copy = strings?.withoutOrders;
	const reportStrings = strings?.reports?.withoutOrders;

	// Both dates are REQUIRED by the API — this report is defined by its window, so there is no
	// sensible default-to-everything. Last full month is the most common question.
	const [range, setRange] = useState<[Dayjs, Dayjs] | null>([
		dayjs().subtract(1, "month").startOf("month"),
		dayjs().subtract(1, "month").endOf("month"),
	]);
	const [includeNeverOrdered, setIncludeNeverOrdered] = useState(false);
	const [search, setSearch] = useState("");

	const [loading, setLoading] = useState(false);
	const [exporting, setExporting] = useState(false);
	const [report, setReport] = useState<ClientsWithoutOrdersReport | null>(
		null
	);
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
	// The filter set that produced the result on screen. Paging and sorting re-query with
	// THIS, not with the controls above, so editing a filter without pressing Generate can
	// never leak into a page change.
	const [appliedQuery, setAppliedQuery] = useState<WithoutOrdersQuery | null>(
		null
	);
	const [sortBy, setSortBy] = useState<string | undefined>();
	const [sortDirection, setSortDirection] = useState<
		SortDirection | undefined
	>();

	const rangeMissing = !range?.[0] || !range?.[1];

	const filterQuery = useMemo(
		(): WithoutOrdersQuery => ({
			fromDate: range?.[0] ? toWireDate(range[0]) : undefined,
			toDate: range?.[1] ? toWireDate(range[1]) : undefined,
			includeNeverOrdered: includeNeverOrdered || undefined,
			search: search.trim() || undefined,
		}),
		[range, includeNeverOrdered, search]
	);

	const load = async (
		query: WithoutOrdersQuery,
		nextPage: number,
		nextPageSize: number,
		nextSortBy?: string,
		nextSortDirection?: SortDirection
	) => {
		setLoading(true);
		try {
			const result = await getClientsWithoutOrders({
				...query,
				currentPage: nextPage,
				pageSize: nextPageSize,
				sortBy: nextSortBy,
				sortDirection: nextSortDirection,
			});
			setReport(result);
			setAppliedQuery(query);
			setPage(nextPage);
			setPageSize(nextPageSize);
		} catch {
			// The axios interceptor already toasts the API's message.
		} finally {
			setLoading(false);
		}
	};

	/**
	 * Both dates are required, so the guard lives here rather than in `load` — once a result is on
	 * screen, paging must keep working off the applied query even if the picker is cleared after.
	 */
	const generate = () => {
		if (rangeMissing) {
			return;
		}
		load(filterQuery, 1, pageSize, sortBy, sortDirection);
	};

	const exportXlsx = async () => {
		if (!appliedQuery) {
			return;
		}

		setExporting(true);
		try {
			const blob = await exportClientsWithoutOrders({
				...appliedQuery,
				sortBy,
				sortDirection,
			});
			saveWorkbook(
				blob,
				`kliente-pa-porosi_${dayjs().format("YYYY-MM-DD")}.xlsx`
			);
		} catch (error) {
			const message = await readBlobErrorMessage(error);
			if (message) {
				toast.error(message);
			}
		} finally {
			setExporting(false);
		}
	};

	const labels = {
		code: common?.colCode ?? "Kodi i klientit",
		name: common?.colName ?? "Emri dhe mbiemri",
		business: common?.colBusiness ?? "Biznesi",
		phone: common?.colPhone ?? "Telefoni",
		businessPhone: common?.colBusinessPhone ?? "Telefoni i biznesit",
		clientSince: common?.colClientSince ?? "Klient që nga",
		totalOrders: common?.colTotalOrders ?? "Totali i porosive",
		countrySplit: common?.colCountrySplit ?? "Sipas shtetit",
		unknownCountryHint:
			common?.unknownCountryHint ??
			"Porosi që nuk lidhen me asnjë shtet — zona e tyre nuk zgjidhet deri te shteti. Vlen të kontrollohen.",
	};

	const columns = useMemo<ColumnsType<ClientWithoutOrders>>(
		() => [
			...identityColumns<ClientWithoutOrders>(labels),
			{
				title: copy?.colLastBefore ?? "Porosia e fundit para periudhës",
				dataIndex: "lastOrderBeforeWindow",
				key: "lastOrderBeforeWindow",
				width: 170,
				sorter: true,
				render: (value: string | null) => formatDate(value),
			},
			{
				title: copy?.colFirstEver ?? "Porosia e parë",
				dataIndex: "firstOrderEver",
				key: "firstOrderEver",
				width: 130,
				render: (value: string | null) => formatDate(value),
			},
			{
				// The API's `lastOrderDate` sort key sorts this report on LastOrderEver, so the
				// column key is the API key rather than the field name it displays.
				title: copy?.colLastEver ?? "Porosia e fundit",
				dataIndex: "lastOrderEver",
				key: "lastOrderDate",
				width: 150,
				sorter: true,
				defaultSortOrder: "descend",
				render: (value: string | null) => formatDateTime(value),
			},
			{
				title: copy?.colDaysToday ?? "Ditë pa porosi (sot)",
				dataIndex: "daysWithoutOrdersToday",
				key: "daysWithoutOrders",
				align: "right",
				width: 150,
				sorter: true,
				render: (days: number | null) =>
					typeof days === "number" ? (
						<Tag color={daysTagColor(days)}>
							{formatNumber(days)}
						</Tag>
					) : (
						<Tag>{copy?.neverOrdered ?? "Asnjëherë"}</Tag>
					),
			},
			...countryTotalsColumns<ClientWithoutOrders>(labels),
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[copy, common]
	);

	const rows = report?.page.items ?? [];
	const hasRun = report !== null;

	return (
		<div className="max-w-[1400px] mx-auto px-4 py-6 flex flex-col gap-6">
			<BulkOpsHeader
				title={reportStrings?.label ?? "Klientë pa porosi në periudhë"}
				subtitle={
					reportStrings?.description ??
					"Klientët që nuk kanë regjistruar asnjë porosi mes dy datave"
				}
				backTo="/bulk-order-operations/client-reports"
				backLabel={common?.back ?? "Të gjitha raportet"}
				actions={
					<Button
						icon={<DownloadOutlined />}
						loading={exporting}
						disabled={!hasRun || rows.length === 0}
						onClick={exportXlsx}
					>
						{common?.exportExcel ?? "Shkarko Excel"}
					</Button>
				}
			/>

			<Alert
				type="info"
				showIcon
				message={
					copy?.scopeNote ??
					"Gjen një boshllëk, jo largim: një klient që porositi në janar, heshti në shkurt dhe u kthye në mars del këtu për shkurtin. Kolona “Porosia e fundit” mund të jetë pas periudhës së zgjedhur."
				}
			/>

			<div className="rounded-lg border border-gray-200 p-4 flex flex-col gap-4">
				<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
					<label className="flex flex-col gap-1">
						<span className="text-sm text-gray-600">
							{copy?.rangeLabel ??
								"Periudha që duhet të jetë bosh"}
						</span>
						<RangePicker
							value={range}
							format="DD/MM/YYYY"
							className="w-full"
							disabledDate={(current) =>
								current && current.isAfter(dayjs(), "day")
							}
							onChange={(value) =>
								setRange(
									value?.[0] && value?.[1]
										? [value[0], value[1]]
										: null
								)
							}
						/>
						<span className="text-xs text-gray-400">
							{copy?.rangeHint ??
								"Të dyja datat janë të detyrueshme"}
						</span>
					</label>

					<label className="flex flex-col gap-1">
						<span className="text-sm text-gray-600">
							{common?.searchLabel ?? "Kërko klient"}
						</span>
						<Input
							allowClear
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							onPressEnter={generate}
							prefix={<SearchOutlined />}
							placeholder={
								common?.searchPlaceholder ??
								"Emri, biznesi, telefoni ose kodi"
							}
						/>
					</label>

					<div className="flex flex-col gap-1">
						<span className="text-sm text-gray-600">
							{copy?.includeNeverLabel ??
								"Përfshi klientët që s'kanë porositur kurrë"}
						</span>
						<span className="flex items-center gap-2 h-8">
							<Switch
								checked={includeNeverOrdered}
								onChange={setIncludeNeverOrdered}
							/>
							<span className="text-xs text-gray-400">
								{copy?.includeNeverHint ??
									"I fikur: raporti lexohet si “heshtën”, jo “nuk nisën kurrë”"}
							</span>
						</span>
					</div>
				</div>

				<div>
					<Button
						type="primary"
						style={rangeMissing ? undefined : PRIMARY_BUTTON_STYLE}
						loading={loading}
						disabled={rangeMissing}
						onClick={generate}
					>
						{common?.generate ?? "Gjenero raportin"}
					</Button>
				</div>
			</div>

			{hasRun && (
				<div className="rounded-lg border border-[#4F16C2] bg-[#4F16C2]/5 p-3 flex flex-wrap gap-6 text-sm">
					<span>
						{copy?.summaryClients ?? "Klientë pa porosi"}:{" "}
						<b>{formatNumber(report.page.totalCount)}</b>
					</span>
					<span>
						{copy?.summaryWindow ?? "Periudha"}:{" "}
						<b>
							{formatDate(report.fromDate)} —{" "}
							{formatDate(
								dayjs(report.toDateExclusive)
									.subtract(1, "day")
									.toISOString()
							)}
						</b>
					</span>
					{report.includeNeverOrdered && (
						<span>
							<Tag color="blue">
								{copy?.includingNever ??
									"Përfshirë edhe ata që s'kanë porositur kurrë"}
							</Tag>
						</span>
					)}
				</div>
			)}

			<Table<ClientWithoutOrders>
				rowKey="sellerId"
				size="small"
				columns={columns}
				dataSource={rows}
				loading={loading}
				scroll={{ x: 1500 }}
				onChange={(pagination, _filters, sorter) => {
					if (!appliedQuery) {
						return;
					}

					const { sortBy: nextSortBy, sortDirection: nextDirection } =
						toApiSort(sorter as SorterResult<ClientWithoutOrders>);

					setSortBy(nextSortBy);
					setSortDirection(nextDirection);
					load(
						appliedQuery,
						pagination.current ?? 1,
						pagination.pageSize ?? pageSize,
						nextSortBy,
						nextDirection
					);
				}}
				pagination={{
					current: page,
					pageSize,
					total: report?.page.totalCount ?? 0,
					showSizeChanger: true,
					pageSizeOptions: [20, 50, 100, 200, 500],
					showTotal: (total) =>
						`${formatNumber(total)} ${
							common?.clients ?? "klientë"
						}`,
				}}
				locale={{
					emptyText: hasRun
						? copy?.noRows ??
						  "Çdo klient ka regjistruar të paktën një porosi në këtë periudhë"
						: common?.notGenerated ??
						  "Zgjidh periudhën dhe kliko Gjenero raportin",
				}}
			/>
		</div>
	);
};

export default WithoutOrders;
